import React, { useEffect, useRef, useState } from "react";

/**
 * MyNotes — the DSP's own notes on this person (Getting Good, Nov 2026).
 *
 * One card on the person's page (Nov 1 redesign) — "Your notes on Maris ·
 * 4 · 2 shared · 1 in her profile", with "Last time you said…" folded in —
 * that opens a panel in place (not a route): her notes and her reflections,
 * newest first, each note with what became of it. Until a
 * coordinator has acted, her "Share with your coordinator" choice stays
 * editable here.
 *
 * With nothing to show — no notes or reflections, and nothing said last
 * time — it draws nothing at all (Nov 2). Her first note brings it in.
 *
 * When something of hers went somewhere good since she last looked (to the
 * next shift, or into the profile), one quiet line says so, once. Dismissed
 * or removed notes just show their status. No consumption counts: these are
 * her contributions and what became of them, nothing she opened.
 *
 * `data` is GET /notes/mine: {entries, counts}. The server marks news seen
 * when it answers, so the banner is drawn from this response and won't
 * repeat on the next open.
 */
export function statusWords(key, poss) {
  return ({
    private:    "Just for you",
    shared:     "Shared with your coordinator",
    with_shift: "With the next shift",
    sent:       "Sent to the profile",
    in_profile: `In ${poss} profile`,
    kept:       "Kept by your coordinator",
    not_used:   "Not used",
    off_shift:  "No longer with the shift",
  })[key] || "";
}

export function bannerFor(entries, name) {
  const news = (entries || []).filter(e => e.kind === "note" && e.news);
  if (news.some(e => e.status === "in_profile")) return `Your note made it into ${name}'s profile.`;
  if (news.some(e => e.status === "with_shift")) return "Your note went to the next shift.";
  if (news.some(e => e.status === "sent")) return `Your coordinator is adding your note to ${name}'s profile.`;
  return null;
}

export function notesLine(counts, name, poss) {
  if (!counts || !counts.notes) return "";
  return `${counts.notes} · ${counts.shared} shared · ${counts.in_profile} in ${poss} profile`;
}

export function echoMeta(lastSaid) {
  if (!lastSaid) return "";
  return lastSaid.kind === "impression" && lastSaid.question ? ` (asked “${lastSaid.question}”)` : "";
}

function when(iso) {
  if (!iso) return "";
  const d = new Date(iso); if (Number.isNaN(d.getTime())) return "";
  const days = Math.floor((Date.now() - d.getTime()) / 86400000);
  if (days <= 0) return "Today";
  if (days === 1) return "Yesterday";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function MyNotes({ clientId, clientName, poss = "their", data, onChanged, lastSaid = null }) {
  const [open, setOpen] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState(null);
  // Drawn from the first answer that arrives: the server has marked the news
  // seen by then, so a later refetch (after a save) would not carry it.
  const [banner, setBanner] = useState(null);
  const firstData = useRef(false);
  useEffect(() => {
    if (!data || firstData.current) return;
    firstData.current = true;
    setBanner(bannerFor(data.entries, clientName));
  }, [data, clientName]);
  const entries = data?.entries || [];
  const counts = data?.counts;

  async function setShared(id, shared) {
    setBusyId(id); setError(null);
    try {
      const r = await fetch(`/api/clients/${clientId}/notes/${id}`, {
        method: "PATCH", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ shared }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || "Couldn't change that.");
      onChanged?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  }

  if (entries.length === 0 && !lastSaid?.text && !banner) return null;
  const line = notesLine(counts, clientName, poss);

  return (
    <section style={s.wrap} aria-label="My notes">
      {banner && <p role="status" style={s.banner}>{banner}</p>}
      <button type="button" style={s.card} onClick={() => setOpen(o => !o)} aria-expanded={open}
              disabled={entries.length === 0}>
        <span style={s.cardRow}>
          <span style={s.cardTitle}>Your notes on {clientName}</span>
          {entries.length > 0 && (
            <span style={s.cardCounts}>
              {line}
              <span aria-hidden="true"> {open ? "▴" : "›"}</span>
            </span>
          )}
        </span>
        {lastSaid?.text && (
          <span style={s.echo}>Last time you said: “{lastSaid.text}”{echoMeta(lastSaid)}</span>
        )}
      </button>
      {open && (
        <ul style={s.list}>
          {entries.map(e => (
            <li key={`${e.kind}-${e.id}`} style={s.item}>
              <p style={s.text}>{e.text}</p>
              <p style={s.meta}>
                {when(e.created_at)} · {e.kind === "reflection" ? "your reflection" : statusWords(e.status, poss)}
              </p>
              {e.kind === "note" && e.live && e.used_by?.length > 0 && (
                <p style={s.meta}>Helpful to {e.used_by.join(", ")}</p>
              )}
              {e.kind === "note" && e.editable && (
                <label style={s.check}>
                  <input type="checkbox" checked={!!e.shared} disabled={busyId === e.id}
                         onChange={ev => setShared(e.id, ev.target.checked)} />
                  <span>Share with your coordinator</span>
                </label>
              )}
            </li>
          ))}
        </ul>
      )}
      {error && <p style={s.error}>{error}</p>}
    </section>
  );
}

const s = {
  wrap:   { display: "flex", flexDirection: "column", gap: 6 },
  banner: { margin: 0, fontSize: "var(--text-sm)", color: "var(--color-accent)", fontFamily: "var(--font-serif)" },
  card:   { font: "inherit", width: "100%", background: "var(--color-bg-inset)", border: "none", borderRadius: "var(--radius-lg)", padding: "var(--space-3) var(--space-4)", textAlign: "left", cursor: "pointer", display: "flex", flexDirection: "column", gap: 6, color: "var(--color-text-primary)" },
  cardRow:   { display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12, flexWrap: "wrap" },
  cardTitle: { fontSize: "var(--text-base)", fontWeight: "var(--weight-medium)" },
  cardCounts:{ fontSize: "var(--text-sm)", color: "var(--color-text-secondary)" },
  echo:   { fontFamily: "var(--font-serif)", fontStyle: "italic", fontSize: "var(--text-sm)", color: "var(--color-text-secondary)", lineHeight: 1.45 },
  list:   { listStyle: "none", margin: 0, padding: 0, display: "flex", flexDirection: "column", gap: "var(--space-2)" },
  item:   { background: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "var(--space-3)", display: "flex", flexDirection: "column", gap: 4 },
  text:   { margin: 0, fontFamily: "var(--font-serif)", fontSize: "var(--text-base)", lineHeight: 1.45, color: "var(--color-text-primary)" },
  meta:   { margin: 0, fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" },
  check:  { display: "inline-flex", alignItems: "center", gap: 6, fontSize: "var(--text-sm)", color: "var(--color-text-primary)", minHeight: "var(--tap-target-min)", cursor: "pointer" },
  error:  { fontSize: "var(--text-sm)", color: "var(--color-critical)", margin: 0 },
};
